import { useCallback } from 'react';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import { useAppContext } from '../../../hooks/useAppContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { isFeatureEnabledForUser } from '../../../services/Features';
import { features } from './features';

export interface EapConfig {
    hash?: string;
}

export type EapState = 'HAS_NONE' | 'HAS_NEW' | 'HAS_ANY';

export const getEapFeatures = (user: CurrentUserDto) =>
    features.filter(feature =>
        feature.type === 'preview' && feature.hideIfDisabled
            ? isFeatureEnabledForUser(feature.featureToggle, user)
            : true
    );

const initialConfig: EapConfig = {};

export const useEapState = (): [EapState, () => void] => {
    const { user } = useAppContext();
    const [config, setConfig] = useLocalStorage<EapConfig>('eap-config', initialConfig);

    const availableFeatures = getEapFeatures(user);

    const featuresHashed = availableFeatures.map(feature => simpleHash(feature.id).toString());
    const hash = featuresHashed.sort((a, b) => a.localeCompare(b)).join(',');

    const markVisited = useCallback(() => {
        setConfig({
            hash,
        });
    }, [hash, setConfig]);

    if (!availableFeatures.length) {
        return ['HAS_NONE', markVisited];
    }

    const oldFeatureHashes = (config.hash || '').split(',');
    const hasNew = config.hash !== hash && featuresHashed.some(h => !oldFeatureHashes.includes(h));

    return [hasNew ? 'HAS_NEW' : 'HAS_ANY', markVisited] as const;
};

function simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return hash >>> 0;
}
